import { useSubscribedMutation } from '@setvi/shared/hooks';
import { formatProductBody } from 'pages/auto-rfq/utils';
import { PricingMode, useRfqConfigStore } from 'pages/auto-rfq/store/rfqConfig';
import { useRFQStore } from 'pages/auto-rfq/store';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { useRfqRealtimeBroadcast } from 'pages/auto-rfq/pages/details/hook/realtime';
import {
  AutoRFQQueryKey,
  QuotesQueryKey,
  updateAutoRfqItemMutation
} from 'Services';

export const useUpdateItem = ({
  triggerApis = [QuotesQueryKey.GET_SINGLE, QuotesQueryKey.PREVIEW_PDF]
}: {
  triggerApis?: string[];
} = {}) => {
  const { mutateAsync: updateRfqItem, isLoading: areItemsUpdating } =
    useSubscribedMutation(
      {
        ...updateAutoRfqItemMutation(),
        onerror: () => {}
      },
      [
        ...triggerApis,
        AutoRFQQueryKey.GET_RFQ_ANALYTICS,
        AutoRFQQueryKey.GET_ITEMS_METADATA
      ]
    );

  const { pricing } = useRfqConfigStore();
  const { user } = useAppContext();
  const { broadcastItemUpdate } = useRfqRealtimeBroadcast();
  const updateProcessedProduct = useRFQStore(s => s.updateProcessedProduct);

  const handleItemUpdate = async (p: any) => {
    const actorName =
      user?.FullName ||
      `${user?.FirstName || ''} ${user?.LastName || ''}`.trim();

    // Optimistically reflect the current user as the last editor so the
    // "Edited by" badge updates immediately on the editor's own screen. The
    // edit mutation doesn't refetch GET_ITEMS, so without this the badge keeps
    // showing the previous editor until a remount. Server stays source of
    // truth via the next refetch / collaborator broadcast.
    if (p?.id && user?.ID) {
      updateProcessedProduct(p.id, {
        lastModifiedBy: {
          userId: String(user.ID),
          name: actorName,
          email: user.Email
        },
        lastModifiedDate: new Date().toISOString()
      });
    }

    const matchedProduct =
      p?.matchedInternalProducts?.[p?.internalIndex || 0] || null;

    // Manual-entry items have no real sell until the client has full customer pricing
    const omitSell =
      p?.manualEntry && pricing?.mode !== PricingMode.CUSTOMER_PRICING;

    const productForUpdate = {
      ...formatProductBody({
        competitor: p,
        product: matchedProduct,
        selected: false,
        edit: true,
        fileUrl: p?.fileUrl || ''
      }),
      manualEntry: p?.manualEntry || false,
      prevItemPercentage: p?.prevItemPercentage || 0,
      statusId: p?.statusId,
      _id: p.id,
      isMatched: p?.isMatched,
      matchOverride: p?.matchOverride,
      inQuote: p?.inQuote || false,
      ...(omitSell ? {} : { sell: p?.sell || 0 }),
      ...(p?.cost !== null && p?.cost !== undefined
        ? { cost: p?.cost || 0 }
        : {})
    };

    const res = await updateRfqItem({
      rfqItemId: p.id,
      product: productForUpdate
    });

    // Broadcast the change to collaborators so their screens update in real
    // time. The backend isn't emitting `rfq_item.updated` for these writes, so
    // the editor publishes it on the Spaces channel with the post-edit item in
    // `after`; other clients' useRfqLiveUpdates applies it directly (their own
    // echo is filtered by actor id). `p` is the optimistic store item the
    // editor just applied, so it's already enriched.
    if (res && p?.id) {
      const after = useRFQStore.getState().products.find(x => x.id === p.id);
      broadcastItemUpdate(p.id, after || p);
    }

    return res;
  };

  return { handleItemUpdate, areItemsUpdating };
};
